// Recovery for stale code-split chunks after a deploy.
//
// Every deploy replaces the hashed /assets/*.js chunks. A tab that loaded the
// site before the deploy still holds the old chunk map, so its next client
// side navigation imports chunks that no longer exist (404) and React throws
// "Failed to fetch dynamically imported module". The fix is a full page load
// of the URL the visitor was going to: by then the router has already pushed
// that URL, so reloading lands on the right page with the new build.
//
// The reload is guarded per URL in sessionStorage so a chunk that is really
// broken on the new build cannot cause a reload loop: the second failure for
// the same URL inside the window falls through to the ErrorBoundary instead.

const STORAGE_KEY = 've-chunk-reload';
const GUARD_MS = 60_000;

let reloading = false;

export function isChunkLoadError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const { name, message } = error as { name?: string; message?: string };
  const msg = message || '';
  return (
    msg.includes('Failed to fetch dynamically imported module') ||
    msg.includes('error loading dynamically imported module') ||
    msg.includes('Importing a module script failed') ||
    msg.includes('Unable to preload CSS') ||
    msg.includes('Loading chunk') ||
    msg.includes('Loading CSS chunk') ||
    (name === 'TypeError' && msg.includes('fetch'))
  );
}

/**
 * Reloads the current URL once to pick up a new deploy's chunks. Returns true
 * when a reload is under way (the caller should then wait, not show an error),
 * false when the guard says this URL was already retried or storage is not
 * available to guard against a loop.
 */
export function reloadForStaleChunk(): boolean {
  if (typeof window === 'undefined') return false;
  if (reloading) return true;
  const url = window.location.href;
  const now = Date.now();
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    const last = raw ? (JSON.parse(raw) as { url?: string; at?: number }) : null;
    if (last && last.url === url && typeof last.at === 'number' && now - last.at < GUARD_MS) {
      return false;
    }
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ url, at: now }));
  } catch {
    // No storage means no loop guard; show the error UI rather than risk a loop.
    return false;
  }
  reloading = true;
  window.location.reload();
  return true;
}

/** Wraps a dynamic import so a stale-chunk failure reloads instead of erroring. */
export function withChunkReload<T>(loader: () => Promise<T>): () => Promise<T> {
  return () =>
    loader().catch((error: unknown) => {
      // Keep the Suspense fallback up while the page reloads.
      if (isChunkLoadError(error) && reloadForStaleChunk()) return new Promise<T>(() => {});
      throw error;
    });
}

let listening = false;

/**
 * Vite dispatches `vite:preloadError` when a chunk or one of its preloaded
 * dependencies (JS or CSS) fails to load. Start the reload right away; the
 * lazy() wrapper then sees `reloading` and keeps the loader pending.
 */
export function installChunkReloadListener(): void {
  if (typeof window === 'undefined' || listening) return;
  listening = true;
  window.addEventListener('vite:preloadError', (event) => {
    const payload = (event as Event & { payload?: unknown }).payload;
    if (payload === undefined || isChunkLoadError(payload)) reloadForStaleChunk();
  });
}
